import React, { useEffect, useMemo, useState } from 'react';
import { useAppDispatch } from 'hooks';
import classNames from 'classnames';

import { DOCUMENT_TYPES, PAGE_SIZE_SM, SAH_STATEMENT_SUMMARY_CSV_MAX_ROWS } from '~constants/common';

import { getErrorMessage } from '~libs/axiosWrapper';
import { formatCompactCurrency } from '~libs/formatters';
import { useToast } from '~libs/toast';

import { Button, ClickableIcon, ErrorResponse, Toolbar } from '~components';
import { SearchBar } from '~components/form-fields/search';
import DropdownCheckbox from '~components/form-fields/search/DropdownCheckbox';
import { faCalculator, faDollarSign, faHandHoldingHeart, faMagnifyingGlass, faMoneyBill } from '~components/icon';
import { ColumnPickerModal, TablePagination, useColumnVisibility } from '~components/table-view';
import Statistics, { StatisticConfig } from '~components/visualisation/statistic/Statistics';

import { COLUMN_GROUPS, exportColumnsFor } from '~support-at-home/forms/dashboard/statementsDashboardList';
import { searchFields } from '~support-at-home/forms/dashboard/statementsDashboardSearch';

import { getAll as getAllUsers } from '~care-workers/actions';
import { getAll as getClients } from '~main/actions/clients';
import { CreateDocumentBatchJob, useCreateDocumentBatchMutation } from '~main/actions/documentBatches';
import { useGetStatementBudgetsSummarySpecsQuery } from '~support-at-home/actions/statementBudgetsSummary';

import StatementBudgetsTable from './StatementBudgetsTable';
import { useStatementBudgetSummary } from './useStatementBudgetSummary';

// Identifies this table's saved column preference row; must match tp-assist-api's table_key.
const TABLE_KEY = 'sah-statement-summary';

// The table groups balances by funding type while the CSV has a row per funding type, so most
// picker columns only exist in the file; say so, or unticking them looks like it did nothing.
const COLUMN_PICKER_DESCRIPTION =
  'Sets the columns in the CSV export. Care Recipient ID, Care Manager and Statement Period also show in the table.';

const ICON_COLOURS = {
  green: '#008000',
  red: '#ff0000',
  blue: '#1565c0',
  warning: '#faa51a',
} as const;

export const StatementsDashboard: React.FC<{ isActive: boolean }> = ({ isActive }) => {
  const dispatch = useAppDispatch();
  const { showToast } = useToast();
  const [createDocumentBatch, { isLoading: isGenerating }] = useCreateDocumentBatchMutation();
  const [searchBarOpen, setSearchBarOpen] = useState(false);
  const [isColumnPickerOpen, setIsColumnPickerOpen] = useState(false);

  const {
    searchParams,
    search,
    filters,
    exportRowCount,
    currentPage,
    totalPages,
    handlePageChange,
    visibleColumns,
    setVisibleColumns,
    columnFilterItems,
    visibleLabels,
    rows,
    summary,
    breakdowns,
    totalCount,
    isLoading,
    isFetching,
    isError,
    error,
  } = useStatementBudgetSummary(isActive);

  useEffect(() => {
    if (isActive) {
      dispatch(getClients({ disabled: undefined, attributes: ['id', 'full_name', 'non_chargeable', 'disabled'] }));
      dispatch(getAllUsers());
    }
  }, [isActive]);

  const {
    data: specsData,
    isLoading: isLoadingSpecs,
    isError: isErrorSpecs,
    refetch: refetchSpecs,
  } = useGetStatementBudgetsSummarySpecsQuery(undefined, { skip: !isActive });
  const {
    isEnabled: isColumnPickerEnabled,
    visibleColumnIds,
    setVisibleColumnIds,
  } = useColumnVisibility({
    tableKey: TABLE_KEY,
    columnSpecs: specsData?.columns,
    onPreferencesChanged: refetchSpecs,
    onError: (columnPreferenceError) => {
      const message =
        columnPreferenceError instanceof Error ? columnPreferenceError.message : 'Failed to save column preferences';
      showToast('danger', message);
    },
  });

  // A specs response with no columns array (e.g. an older API mid-rollout) cannot drive an export
  const hasIncompleteSpecs = isActive && !isLoadingSpecs && !isErrorSpecs && !specsData?.columns;
  const exportColumns = useMemo(() => exportColumnsFor(specsData?.columns ?? []), [specsData]);
  const visibleSpecs = useMemo(() => (specsData?.columns ?? []).filter((spec) => spec.visible), [specsData]);

  const onGenerateCsv = async () => {
    // The button is disabled until the row count is known, so this guard always has a count to check
    if (exportRowCount !== undefined && exportRowCount > SAH_STATEMENT_SUMMARY_CSV_MAX_ROWS) {
      showToast(
        'danger',
        `This export has ${exportRowCount.toLocaleString()} rows, over the ${SAH_STATEMENT_SUMMARY_CSV_MAX_ROWS.toLocaleString()} row limit. Narrow the date range and export again.`,
      );
      return;
    }
    try {
      const job = {
        document_type: DOCUMENT_TYPES.SAH_STATEMENT_SUMMARY.CSV,
        date_from: filters.date_from,
        date_to: filters.date_to,
        client_ids: searchParams?.client_id ?? [],
        case_manager_ids: searchParams?.case_manager ?? [],
        columns: exportColumns,
      } as CreateDocumentBatchJob & Record<string, unknown>;
      await createDocumentBatch([job]).unwrap();
      showToast('success', 'CSV generation started');
    } catch (generateError) {
      const message = generateError instanceof Error ? generateError.message : 'Failed to start CSV generation';
      showToast('danger', message);
    }
  };

  const statistics: StatisticConfig[] | null =
    summary && !isLoading
      ? [
          {
            title: 'Total Opening Balance',
            type: 'value',
            value: formatCompactCurrency(summary.total_opening_balance),
            icon: faMoneyBill,
            iconColor: ICON_COLOURS.green,
            breakdown: breakdowns.opening,
            dataCy: 'statements-dashboard-total-opening',
          },
          {
            title: 'Total Spent',
            type: 'value',
            value: formatCompactCurrency(summary.total_spent),
            icon: faDollarSign,
            iconColor: ICON_COLOURS.red,
            breakdown: breakdowns.spent,
            dataCy: 'statements-dashboard-total-spent',
          },
          {
            title: 'Total Closing Balance',
            type: 'value',
            value: formatCompactCurrency(summary.total_closing_balance),
            icon: faCalculator,
            iconColor: ICON_COLOURS.blue,
            breakdown: breakdowns.closing,
            dataCy: 'statements-dashboard-total-closing',
          },
          {
            title: 'Total Co-Contributions',
            type: 'value',
            value: formatCompactCurrency(summary.total_co_contributions),
            icon: faHandHoldingHeart,
            iconColor: ICON_COLOURS.warning,
            dataCy: 'statements-dashboard-total-co-contributions',
          },
        ]
      : null;

  return (
    <div
      className={classNames('statements-dashboard', { fetching: isFetching })}
      data-cy="statements-dashboard"
      aria-busy={isFetching}
    >
      <Toolbar
        id="statements-dashboard"
        rightCluster={
          <>
            <ClickableIcon
              className="me-2"
              icon={faMagnifyingGlass}
              id="statements-search-button"
              onClick={() => setSearchBarOpen(!searchBarOpen)}
              toolTip="Toggle Search Bar"
              ariaLabel="Toggle Search Bar"
              ariaExpanded={searchBarOpen}
            />
            {columnFilterItems.length > 0 && (
              <DropdownCheckbox
                caption="Filter"
                items={columnFilterItems}
                values={visibleColumns}
                onClick={setVisibleColumns}
                dataCy="statements-dashboard-column-filter"
              />
            )}
            <Button
              size="sm"
              color="secondary"
              loading={isGenerating}
              // No row count (summary loading, refetching for new filters, or refused) means the
              // row-limit guard can't run, so the export waits for it
              disabled={
                isGenerating ||
                isLoadingSpecs ||
                isErrorSpecs ||
                hasIncompleteSpecs ||
                exportColumns.length === 0 ||
                exportRowCount === undefined
              }
              onClick={onGenerateCsv}
              dataCy="statements-dashboard-generate-csv"
            >
              Generate CSV
            </Button>
            {isColumnPickerEnabled && (
              <Button
                size="sm"
                color="secondary"
                disabled={isLoadingSpecs || isErrorSpecs || hasIncompleteSpecs}
                onClick={() => setIsColumnPickerOpen(true)}
                dataCy="statements-dashboard-column-picker-button"
              >
                Columns
              </Button>
            )}
          </>
        }
      />
      <SearchBar
        id="statements-searchbar"
        isOpen={searchBarOpen}
        fields={searchFields}
        initialValues={searchParams}
        onSubmit={search}
        storageKey="statements-dashboard"
      />
      {isError && (
        <ErrorResponse
          message={`Failed to load statement budget summary - ${getErrorMessage(error)}`}
          dataCy="statements-dashboard-error"
        />
      )}
      {!isError && (
        <>
          {statistics && (
            <div className="statements-summary">
              <Statistics statistics={statistics} />
            </div>
          )}
          <TablePagination
            currentPage={currentPage}
            totalPages={totalPages}
            pageSize={PAGE_SIZE_SM}
            totalCount={totalCount}
            onPageChange={handlePageChange}
            isLoading={isFetching}
            showPageSizeOptions={false}
          />
          <StatementBudgetsTable
            rows={rows}
            isLoading={isLoading}
            visibleFundingTypes={visibleLabels}
            visibleColumns={specsData?.columns ? visibleSpecs : null}
          />
        </>
      )}
      {isColumnPickerEnabled && (
        <ColumnPickerModal
          isOpen={isColumnPickerOpen}
          setIsOpen={setIsColumnPickerOpen}
          columns={specsData?.columns ?? []}
          groups={COLUMN_GROUPS}
          visibleColumnIds={visibleColumnIds}
          onApply={setVisibleColumnIds}
          description={COLUMN_PICKER_DESCRIPTION}
          dataCy="statements-dashboard-column-picker"
        />
      )}
    </div>
  );
};
